import { fromJS } from 'immutable'
import { RESTRICTED_PROJECTS_REDUCER_KEYS as KEYS } from './restrictedProjectsReducerKeys'
import {
  API_EVENT,
  isPMApiDetailedAction,
  parseApiActionType,
  PERSIST_REHYDRATE_ACTION_TYPE,
} from '../helpers/reducerHelper'
import { PM_API_RESOURCES } from '../constants'

const base = fromJS({
  [KEYS.BY_PROJECT_ID]: {},
  [KEYS.LOADING_STATES]: {},
  [KEYS.ERROR_STATES]: {},
})

const restrictedProjectsReducer = (state = base, action) => {
  // Handle persistence rehydration
  if (action.type === PERSIST_REHYDRATE_ACTION_TYPE) {
    return state
  }

  // Handle PM API actions
  if (isPMApiDetailedAction(action)) {
    const { resource, event, meta } = parseApiActionType(action.type)

    switch (resource) {
      case PM_API_RESOURCES.PROJECT_RESTRICTIONS:
        return handleProjectRestrictionsAction(state, action, event, meta)
      
      case PM_API_RESOURCES.PROJECT_ADD_RESTRICTION:
        return handleAddRestrictionAction(state, action, event, meta)
      
      case PM_API_RESOURCES.PROJECT_REMOVE_RESTRICTION:
        return handleRemoveRestrictionAction(state, action, event, meta)
      
      default:
        return state
    }
  }

  return state
}

const handleProjectRestrictionsAction = (state, action, event, meta) => {
  const { projectId } = meta || {}
  
  if (!projectId) {
    return state
  }

  switch (event) {
    case API_EVENT.REQUEST:
      return state
        .setIn([KEYS.LOADING_STATES, projectId], true)
        .deleteIn([KEYS.ERROR_STATES, projectId])

    case API_EVENT.SUCCESS: {
      const { payload } = action
      return state
        .setIn([KEYS.BY_PROJECT_ID, projectId], fromJS(payload))
        .setIn([KEYS.LOADING_STATES, projectId], false)
        .deleteIn([KEYS.ERROR_STATES, projectId])
    }

    case API_EVENT.FAILURE:
      return state
        .setIn([KEYS.LOADING_STATES, projectId], false)
        .setIn([KEYS.ERROR_STATES, projectId], action.payload?.message || 'Failed to load restrictions')

    default:
      return state
  }
}

const handleAddRestrictionAction = (state, action, event, meta) => {
  const { projectId } = meta || {}
  
  if (!projectId) {
    return state
  }

  switch (event) {
    case API_EVENT.REQUEST:
      return state
        .setIn([KEYS.LOADING_STATES, `${projectId}_add`], true)
        .deleteIn([KEYS.ERROR_STATES, `${projectId}_add`])

    case API_EVENT.SUCCESS: {
      const { payload } = action
      // Update the restrictions data with the new response
      return state
        .setIn([KEYS.BY_PROJECT_ID, projectId], fromJS(payload))
        .setIn([KEYS.LOADING_STATES, `${projectId}_add`], false)
        .deleteIn([KEYS.ERROR_STATES, `${projectId}_add`])
    }

    case API_EVENT.FAILURE:
      return state
        .setIn([KEYS.LOADING_STATES, `${projectId}_add`], false)
        .setIn([KEYS.ERROR_STATES, `${projectId}_add`], action.payload?.message || 'Failed to add restrictions')

    default:
      return state
  }
}

const handleRemoveRestrictionAction = (state, action, event, meta) => {
  const { projectId } = meta || {}
  
  if (!projectId) {
    return state
  }

  switch (event) {
    case API_EVENT.REQUEST:
      return state
        .setIn([KEYS.LOADING_STATES, `${projectId}_remove`], true)
        .deleteIn([KEYS.ERROR_STATES, `${projectId}_remove`])

    case API_EVENT.SUCCESS: {
      const { payload } = action
      // Update the restrictions data with the new response
      return state
        .setIn([KEYS.BY_PROJECT_ID, projectId], fromJS(payload))
        .setIn([KEYS.LOADING_STATES, `${projectId}_remove`], false)
        .deleteIn([KEYS.ERROR_STATES, `${projectId}_remove`])
    }

    case API_EVENT.FAILURE:
      return state
        .setIn([KEYS.LOADING_STATES, `${projectId}_remove`], false)
        .setIn([KEYS.ERROR_STATES, `${projectId}_remove`], action.payload?.message || 'Failed to remove restrictions')

    default:
      return state
  }
}

export default restrictedProjectsReducer